import { requestTaskPage, requestTaskPrefix } from '../platform/taskList';
import { reconcileList } from '../platform/reconcileList';
import { create } from 'zustand';
import type { DeleteOutputsResult, Task, CreateTaskParams, ImageRole, TaskStatus } from '../types';
import { captureSessionEpoch, isSessionEpochCurrent } from '../platform/sessionEpoch';

interface TaskState {
  tasks: Task[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  loadMoreTasks: () => Promise<void>;
  loadTasks: (maxAgeMs?: number) => Promise<void>;

  createTask: (params: CreateTaskParams) => Promise<Task>;
  cancelTask: (id: string) => Promise<void>;
  retryTask: (id: string, outputRoles?: ImageRole[]) => Promise<boolean>;
  deleteTask: (id: string) => Promise<void>;
  deleteTaskOutputs: (outputIds: string[]) => Promise<DeleteOutputsResult>;
  clearCompleted: () => Promise<void>;
  updateTaskStatus: (taskId: string, status: TaskStatus, errorMessage?: string) => void;
  updateTaskProgress: (taskId: string, progress: number, message: string) => void;
  updateTaskOutputStatus: (
    taskId: string,
    outputId: string,
    status: 'pending' | 'running' | 'success' | 'failed',
    errorMessage?: string,
    updatedAt?: string | number,
  ) => void;
  reset: () => void;
}

let loadSequence = 0;
let taskEpoch = 0;

let tasksCursor: string | null = null;
let tasksLoadedCount = 100;
let activeTaskMore: { epoch: ReturnType<typeof captureTaskEpoch>; promise: Promise<void> } | undefined;
let lastTaskLoad: { at: number; epoch: ReturnType<typeof captureTaskEpoch> } | undefined;
let activeTaskLoad: { epoch: ReturnType<typeof captureTaskEpoch>; promise: Promise<void> } | undefined;

function captureTaskEpoch() {
  return { store: taskEpoch, session: captureSessionEpoch() };
}

function isTaskEpochCurrent(epoch: ReturnType<typeof captureTaskEpoch>): boolean {
  return epoch.store === taskEpoch && isSessionEpochCurrent(epoch.session);
}
const recentlyCreatedTasks = new Map<string, number>();
const recentOutputUpdates = new Map<string, {
  taskId: string;
  status: 'pending' | 'running' | 'success' | 'failed';
  errorMessage?: string;
  updatedAt?: number;
  expiresAt: number;
}>();
const recentTaskUpdates = new Map<string, {
  status?: TaskStatus;
  errorMessage?: string;
  progress?: number;
  progressMessage?: string;
  expiresAt: number;
}>();
const RECENT_TASK_GRACE_MS = 30_000;
const LIVE_OUTPUT_GRACE_MS = 5_000;

export function mergeRecentTaskUpdates(tasks: Task[], now: number): Task[] {
  for (const [outputId, update] of recentOutputUpdates) {
    if (update.expiresAt <= now) recentOutputUpdates.delete(outputId);
  }
  for (const [taskId, update] of recentTaskUpdates) {
    if (update.expiresAt <= now) recentTaskUpdates.delete(taskId);
  }
  return tasks.map((task) => {
    const liveTask = recentTaskUpdates.get(task.id);
    const mergedOutputs = task.outputs.map((output) => {
      const update = recentOutputUpdates.get(output.id);
      if (!update || update.taskId !== task.id) return output;
      if (update.updatedAt && output.updatedAt && output.updatedAt >= update.updatedAt) {
        recentOutputUpdates.delete(output.id);
        return output;
      }
      return {
        ...output,
        status: update.status,
        errorMessage: update.status === 'failed' ? update.errorMessage : undefined,
        image: undefined,
        mediaAvailability: undefined,
        updatedAt: update.updatedAt || output.updatedAt,
      };
    });
    if (!liveTask) return { ...task, outputs: mergedOutputs };
    const progress = liveTask.progress === undefined
      ? task.progress
      : liveTask.status === 'pending'
        ? liveTask.progress
        : Math.max(task.progress, liveTask.progress);
    return {
      ...task,
      status: liveTask.status || task.status,
      errorMessage: liveTask.status ? liveTask.errorMessage : task.errorMessage,
      progress,
      progressMessage: liveTask.progressMessage || task.progressMessage,
      outputs: mergedOutputs,
    };
  });
}

export const useTaskStore = create<TaskState>((set, get) => ({
  tasks: [],
  isLoading: false,
  isLoadingMore: false,
  hasMore: false,

  loadTasks: async (maxAgeMs = 0) => {
    // Page mounts may reuse an already fresh account load. Event/mutation reads
    // use the default zero age and always fetch after their invalidation.
    if (maxAgeMs > 0 && lastTaskLoad && isTaskEpochCurrent(lastTaskLoad.epoch)
      && Date.now() - lastTaskLoad.at < maxAgeMs) return;
    if (maxAgeMs > 0 && activeTaskLoad && isTaskEpochCurrent(activeTaskLoad.epoch)) {
      await activeTaskLoad.promise;
      return;
    }
    const epoch = captureTaskEpoch();
    if (activeTaskMore && isTaskEpochCurrent(activeTaskMore.epoch)) await activeTaskMore.promise.catch(() => undefined);
    if (!isTaskEpochCurrent(epoch)) return;
    const requestSequence = ++loadSequence;
    const promise = (async () => {
      set({ isLoading: true });
      try {
        const page = window.CLOUD_RUNTIME ? await requestTaskPrefix('tasks', tasksLoadedCount, 100) : null;
        const tasks = page ? page.items : await window.electron.invoke(window.IPC_CHANNELS.TASK_LIST);
        if (requestSequence !== loadSequence || !isTaskEpochCurrent(epoch)) return;
        tasksCursor = page?.nextCursor || null;
        set({ hasMore: Boolean(tasksCursor) });
        lastTaskLoad = { at: Date.now(), epoch };
        const now = Date.now();
        const currentTasks = mergeRecentTaskUpdates(tasks as Task[], now);
        const serverIds = new Set(currentTasks.map((task) => task.id));
        set((state) => ({
          tasks: reconcileList(state.tasks, [
            ...state.tasks.filter((task) => {
              const keepUntil = recentlyCreatedTasks.get(task.id) || 0;
              if (serverIds.has(task.id)) {
                recentlyCreatedTasks.delete(task.id);
                return false;
              }
              if (keepUntil > now) return true;
              recentlyCreatedTasks.delete(task.id);
              return false;
            }),
            ...currentTasks,
          ]),
          isLoading: false,
        }));
      } catch (error) {
        console.error('Failed to load tasks:', error);
        if (requestSequence === loadSequence && isTaskEpochCurrent(epoch)) set({ isLoading: false });
      }
    })();
    const entry = { epoch, promise };
    activeTaskLoad = entry;
    try { await promise; }
    finally { if (activeTaskLoad === entry) activeTaskLoad = undefined; }
  },

  loadMoreTasks: async () => {
    if (!window.CLOUD_RUNTIME) return;
    if (activeTaskMore && isTaskEpochCurrent(activeTaskMore.epoch)) return activeTaskMore.promise;
    const epoch = captureTaskEpoch();
    // A newer forced refresh can replace the request while we are waiting.
    // Keep waiting for the current refresh before choosing its cursor.
    while (activeTaskLoad && isTaskEpochCurrent(activeTaskLoad.epoch)) {
      const pending = activeTaskLoad;
      await pending.promise;
      if (!isTaskEpochCurrent(epoch)) return;
      if (activeTaskLoad === pending) break;
    }
    if (activeTaskMore && isTaskEpochCurrent(activeTaskMore.epoch)) return activeTaskMore.promise;
    if (!isTaskEpochCurrent(epoch) || !tasksCursor) return;
    const sequence = loadSequence;
    const cursor = tasksCursor;
    set({ isLoadingMore: true });
    const promise = (async () => {
      try {
        const page = await requestTaskPage('tasks', 100, cursor);
        if (sequence !== loadSequence || !isTaskEpochCurrent(epoch)) return;
        tasksCursor = page.nextCursor;
        tasksLoadedCount += page.items.length;
        set(state => {
          const ids = new Set(state.tasks.map(item => item.id));
          return { tasks: [...state.tasks, ...page.items.filter(item => !ids.has(item.id))], hasMore: Boolean(tasksCursor) };
        });
      } finally { if (isTaskEpochCurrent(epoch)) set({ isLoadingMore: false }); }
    })();
    const entry = { epoch, promise };
    activeTaskMore = entry;
    try { await promise; }
    finally { if (activeTaskMore === entry) activeTaskMore = undefined; }
  },
  createTask: async (params) => {
    const epoch = captureTaskEpoch();
    const task = await window.electron.invoke(window.IPC_CHANNELS.TASK_CREATE, params);
    if (isTaskEpochCurrent(epoch)) {
      recentlyCreatedTasks.set(task.id, Date.now() + RECENT_TASK_GRACE_MS);
      set((state) => ({ tasks: [task, ...state.tasks.filter((item) => item.id !== task.id)] }));
    }
    return task;
  },

  cancelTask: async (id) => {
    const epoch = captureTaskEpoch();
    await window.electron.invoke(window.IPC_CHANNELS.TASK_CANCEL, { id });
    if (!isTaskEpochCurrent(epoch)) return;
    set((state) => ({
      tasks: state.tasks.map((task) => task.id === id ? {
        ...task,
        status: 'cancelled' as TaskStatus,
        errorMessage: '任务已取消',
        outputs: task.outputs.map((output) =>
          ['pending', 'running'].includes(output.status)
            ? { ...output, status: 'failed' as const, errorMessage: '任务已取消' }
            : output
        ),
      } : task),
    }));
  },

  retryTask: async (id, outputRoles) => {
    const epoch = captureTaskEpoch();
    const result = await window.electron.invoke(window.IPC_CHANNELS.TASK_RETRY, { id, outputRoles });
    if (!result?.queued || !isTaskEpochCurrent(epoch)) return false;
    set((state) => ({
      tasks: state.tasks.map((t) =>
        t.id === id
          ? { ...t, status: 'pending' as TaskStatus, progress: 0, errorMessage: undefined, retryCount: 0 }
          : t
      ),
    }));
    return true;
  },

  deleteTask: async (id) => {
    const epoch = captureTaskEpoch();
    await window.electron.invoke(window.IPC_CHANNELS.TASK_DELETE, { id });
    if (!isTaskEpochCurrent(epoch)) return;
    set((state) => ({
      tasks: state.tasks.filter((t) => t.id !== id),
    }));
    if (window.CLOUD_RUNTIME) await get().loadTasks();
  },

  deleteTaskOutputs: async (outputIds) => {
    const epoch = captureTaskEpoch();
    const result = await window.electron.invoke(window.IPC_CHANNELS.RESULT_DELETE_OUTPUTS, { outputIds });
    if (!isTaskEpochCurrent(epoch)) return normalizeDeleteResult(result);
    const idSet = new Set(outputIds);
    const removedTaskIds = new Set([
      ...(result.hiddenTaskIds || []),
      ...(result.cleanedTaskIds || []),
    ]);
    set((state) => ({
      tasks: state.tasks
        .filter((task) => !removedTaskIds.has(task.id))
        .map((task) => ({
          ...task,
          outputs: task.outputs.filter((output) => !idSet.has(output.id)),
        }))
        .filter((task) => task.outputs.length > 0 || !['success', 'partial_success', 'failed'].includes(task.status)),
    }));
    await get().loadTasks();
    return normalizeDeleteResult(result);
  },

  clearCompleted: async () => {
    const epoch = captureTaskEpoch();
    await window.electron.invoke(window.IPC_CHANNELS.TASK_CLEAR_COMPLETED);
    if (!isTaskEpochCurrent(epoch)) return;
    set((state) => ({
      tasks: state.tasks.filter(
        (t) => !['success', 'failed', 'partial_success', 'cancelled'].includes(t.status)
      ),
    }));
    if (window.CLOUD_RUNTIME) await get().loadTasks();
  },

  updateTaskStatus: (taskId, status, errorMessage) => {
    const previous = recentTaskUpdates.get(taskId);
    recentTaskUpdates.set(taskId, {
      ...previous,
      status,
      errorMessage,
      progress: status === 'pending' ? 0 : previous?.progress,
      progressMessage: status === 'pending' ? '等待任务队列' : previous?.progressMessage,
      expiresAt: Date.now() + LIVE_OUTPUT_GRACE_MS,
    });
    set((state) => ({
      tasks: state.tasks.map((t) =>
        t.id === taskId ? {
          ...t,
          status,
          errorMessage,
          progressMessage: ['pending', 'validating', 'running'].includes(status)
            ? t.progressMessage
            : undefined,
        } : t
      ),
    }));
  },

  updateTaskProgress: (taskId, progress, message) => {
    const previous = recentTaskUpdates.get(taskId);
    recentTaskUpdates.set(taskId, {
      ...previous,
      progress,
      progressMessage: message || previous?.progressMessage,
      expiresAt: Date.now() + LIVE_OUTPUT_GRACE_MS,
    });
    set((state) => ({
      tasks: state.tasks.map((t) =>
        t.id === taskId ? { ...t, progress, progressMessage: message || t.progressMessage } : t
      ),
    }));
  },

  updateTaskOutputStatus: (taskId, outputId, status, errorMessage, updatedAt) => {
    const normalizedUpdatedAt = updatedAt == null
      ? undefined
      : typeof updatedAt === 'number'
        ? updatedAt
        : new Date(updatedAt).getTime();
    recentOutputUpdates.set(outputId, {
      taskId,
      status,
      errorMessage: status === 'failed' ? errorMessage : undefined,
      updatedAt: Number.isFinite(normalizedUpdatedAt) ? normalizedUpdatedAt : undefined,
      expiresAt: Date.now() + LIVE_OUTPUT_GRACE_MS,
    });
    set((state) => ({
      tasks: state.tasks.map((task) => task.id === taskId ? {
        ...task,
        outputs: task.outputs.map((output) => output.id === outputId ? {
          ...output,
          status,
          errorMessage: status === 'failed' ? errorMessage : undefined,
          image: undefined,
          mediaAvailability: undefined,
          updatedAt: Number.isFinite(normalizedUpdatedAt) ? normalizedUpdatedAt : output.updatedAt,
        } : output),
      } : task),
    }));
  },

  reset: () => {
    tasksCursor = null;
    tasksLoadedCount = 100;
    activeTaskMore = undefined;
    lastTaskLoad = undefined;
    activeTaskLoad = undefined;
    ++taskEpoch;
    ++loadSequence;
    recentlyCreatedTasks.clear();
    recentOutputUpdates.clear();
    recentTaskUpdates.clear();
    set({ tasks: [], isLoading: false, isLoadingMore: false, hasMore: false });
  },
}));

function normalizeDeleteResult(result: Partial<DeleteOutputsResult>): DeleteOutputsResult {
  return {
    deleted: result.deleted || 0,
    mode: result.mode === 'hidden' ? 'hidden' : 'cleaned',
    hiddenTaskIds: result.hiddenTaskIds || [],
    cleanedTaskIds: result.cleanedTaskIds || [],
  };
}
